import { Table as TiptapTable, TableView, createColGroup } from "@tiptap/extension-table";
import TiptapTableCell from "@tiptap/extension-table-cell";
import TiptapTableHeader from "@tiptap/extension-table-header";
import { mergeAttributes } from "@tiptap/core";

// The stock prosemirror-tables columnResizing plugin renders every table
// through `options.View` (TableView) directly — it builds its own bare
// <table>/<colgroup>/<tbody> DOM and never calls `renderHTML` for the
// EDITOR's own DOM. This subclass re-applies our width/align/border/bg
// styling on top of the stock view, both on initial render and on every
// `update()`, so what the admin sees while editing matches the public
// render exactly.
function applyTableStyling(table, node) {
  const { widthMode, widthValue, align, bgColor, borderColor, borderWidth, cellPadding, cellHeight } = node.attrs;
  const widthCss = widthMode === "fixed" ? `${widthValue || 0}px` : `${widthValue || 100}%`;
  table.className = `slh-table tbl-align-${align || "left"}`;
  table.style.setProperty("--tbl-border-color", borderColor || "#d1d5db");
  table.style.setProperty("--tbl-border-width", `${borderWidth || "1"}px`);
  if (bgColor) table.style.setProperty("--tbl-bg", bgColor);
  else table.style.removeProperty("--tbl-bg");
  table.style.setProperty("--tbl-cell-padding", cellPadding ? `${cellPadding}px` : "0.5rem 0.75rem");
  table.style.setProperty("--tbl-cell-height", cellHeight ? `${cellHeight}px` : "auto");
  // Runs after updateColumns(), which otherwise resets width/min-width
  // based on the columns' own fixed pixel widths — our explicit table-wide
  // width always wins over that.
  table.style.width = widthCss;
  table.style.minWidth = "";
}

class SlhTableView extends TableView {
  constructor(node, cellMinWidth) {
    super(node, cellMinWidth);
    applyTableStyling(this.table, node);
  }
  update(node) {
    const result = super.update(node);
    if (result) {
      applyTableStyling(this.table, node);
      // The stock TableView's updateColumns() only ever *sets* min-width
      // when a column's colwidth is cleared — it never removes a
      // previously-set explicit `width`, so a column that was manually
      // dragged wide stays visually wide even after colwidth is reset to
      // null (e.g. by the "Equal Column Widths" button). Clear it here.
      const row = node.firstChild;
      if (row) {
        let col = 0;
        let colEl = this.colgroup.firstChild;
        for (let i = 0; i < row.childCount; i += 1) {
          const { colspan, colwidth } = row.child(i).attrs;
          for (let j = 0; j < colspan; j += 1, col += 1) {
            if (colEl && !(colwidth && colwidth[j])) {
              colEl.style.removeProperty("width");
            }
            colEl = colEl?.nextSibling;
          }
        }
      }
    }
    return result;
  }
}

// Table node extended with page-builder level properties — overall width
// (fixed px or % of the surrounding content area), justification, border
// colour/thickness and background colour. These are table-wide (not
// per-cell) so the "Table Properties" dialog stays a single simple form.
// Per-column resize (drag the column edge) still works via the stock
// `resizable: true` option, independent of these attrs.
export const SlhTable = TiptapTable.extend({
  addOptions() {
    return {
      ...this.parent?.(),
      View: SlhTableView,
    };
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      widthMode: {
        default: "percent",
        parseHTML: (el) => el.getAttribute("data-width-mode") || "percent",
        renderHTML: () => ({}),
      },
      widthValue: {
        default: "100",
        parseHTML: (el) => el.getAttribute("data-width-value") || "100",
        renderHTML: () => ({}),
      },
      align: {
        default: "left",
        parseHTML: (el) => el.getAttribute("data-align") || "left",
        renderHTML: () => ({}),
      },
      bgColor: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-bg-color") || null,
        renderHTML: () => ({}),
      },
      borderColor: {
        default: "#d1d5db",
        parseHTML: (el) => el.getAttribute("data-border-color") || "#d1d5db",
        renderHTML: () => ({}),
      },
      borderWidth: {
        default: "1",
        parseHTML: (el) => el.getAttribute("data-border-width") || "1",
        renderHTML: () => ({}),
      },
      cellPadding: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-cell-padding") || "",
        renderHTML: () => ({}),
      },
      cellHeight: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-cell-height") || "",
        renderHTML: () => ({}),
      },
    };
  },

  renderHTML({ node, HTMLAttributes }) {
    const { widthMode, widthValue, align, bgColor, borderColor, borderWidth, cellPadding, cellHeight } = node.attrs;
    const widthCss = widthMode === "fixed" ? `${widthValue || 0}px` : `${widthValue || 100}%`;
    const styleParts = [
      `width: ${widthCss}`,
      `--tbl-border-color: ${borderColor || "#d1d5db"}`,
      `--tbl-border-width: ${borderWidth || "1"}px`,
      `--tbl-cell-padding: ${cellPadding ? `${cellPadding}px` : "0.5rem 0.75rem"}`,
      `--tbl-cell-height: ${cellHeight ? `${cellHeight}px` : "auto"}`,
    ];
    if (bgColor) styleParts.push(`--tbl-bg: ${bgColor}`);

    // Carry the drag-resized column widths through to the public render —
    // without this colgroup, `colwidth` on each cell has no CSS effect.
    const { colgroup } = createColGroup(node, this.options.cellMinWidth);

    const attrs = mergeAttributes(HTMLAttributes, {
      class: `slh-table tbl-align-${align || "left"}`,
      "data-width-mode": widthMode || "percent",
      "data-width-value": widthValue || "100",
      "data-align": align || "left",
      "data-border-color": borderColor || "#d1d5db",
      "data-border-width": borderWidth || "1",
      ...(bgColor ? { "data-bg-color": bgColor } : {}),
      ...(cellPadding ? { "data-cell-padding": cellPadding } : {}),
      ...(cellHeight ? { "data-cell-height": cellHeight } : {}),
      style: `${styleParts.join("; ")};`,
    });

    return colgroup ? ["table", attrs, colgroup, ["tbody", 0]] : ["table", attrs, ["tbody", 0]];
  },
});

export default SlhTable;

// Per-cell content justification — horizontal (align) and vertical
// (valign), independent of the table-wide properties above. Shared
// attribute shape between <td> and <th> so the "Cell Alignment" dialog in
// TiptapEditor.jsx can read/write either one the same way.
const cellAlignAttributes = {
  align: {
    default: null,
    parseHTML: (el) => el.getAttribute("data-cell-align") || null,
    renderHTML: (attrs) => (attrs.align ? { "data-cell-align": attrs.align, style: `text-align: ${attrs.align};` } : {}),
  },
  valign: {
    default: null,
    parseHTML: (el) => el.getAttribute("data-cell-valign") || null,
    renderHTML: (attrs) => (attrs.valign ? { "data-cell-valign": attrs.valign, style: `vertical-align: ${attrs.valign};` } : {}),
  },
};

export const SlhTableCell = TiptapTableCell.extend({
  addAttributes() {
    return { ...this.parent?.(), ...cellAlignAttributes };
  },
});

export const SlhTableHeader = TiptapTableHeader.extend({
  addAttributes() {
    return { ...this.parent?.(), ...cellAlignAttributes };
  },
});
